import React, { useEffect, useRef, useState } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import QuestionMarkOutlinedIcon from "@mui/icons-material/QuestionMarkOutlined";
import { Button, CircularProgress, Dialog } from "@mui/material";
import {
  InvoiceDialogCloseButton,
  invoiceDesignFontFamily,
  invoiceDesignTokens,
  invoiceDialogPaperSx,
  invoiceModalActionSx,
} from "../InvoiceDesignSystem";
import {
  getValidInvoiceEmailRecipient,
  isInvoiceEmailDeliveryPendingError,
} from "../invoiceDeliveryFeedback";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  NETWORK_ERROR_TEXT,
} from "../../../../utils/userFacingError";
import {
  getInvoiceNoSavedEmailText,
  INVOICE_EMAIL_UNCONFIRMED_TEXT,
} from "../invoiceEmailText";

export type ManageInvoiceActionConfirmationKind =
  | "CREDIT_REFUND"
  | "DELETE_DRAFT"
  | "EMAIL_INVOICE"
  | "EMAIL_RECEIPT"
  | "PROCESS_PAYMENT"
  | "RESEND_RECEIPT";

// "We could not [task]." for a failed confirm that is not an email.
const confirmationTaskByKind: Record<
  ManageInvoiceActionConfirmationKind,
  string
> = {
  CREDIT_REFUND: "credit or refund the invoice",
  DELETE_DRAFT: "delete the draft invoice",
  EMAIL_INVOICE: "email the invoice",
  EMAIL_RECEIPT: "email the receipt",
  PROCESS_PAYMENT: "open Process Payment",
  RESEND_RECEIPT: "resend the receipt",
};

// Copy follows the Claude Design InvoiceConfirmModal COPY table.
const copyByKind: Record<
  ManageInvoiceActionConfirmationKind,
  {
    confirmLabel: string;
    heading?: string;
  }
> = {
  CREDIT_REFUND: {
    confirmLabel: "Yes",
    heading: "Credit/Refund Paid Invoice",
  },
  DELETE_DRAFT: {
    confirmLabel: "Delete",
    heading: "Delete Draft Invoice",
  },
  EMAIL_INVOICE: {
    confirmLabel: "Yes, Send",
    heading: "Email Invoice",
  },
  EMAIL_RECEIPT: {
    confirmLabel: "Yes, Send",
    heading: "Email Receipt",
  },
  PROCESS_PAYMENT: {
    confirmLabel: "Proceed",
  },
  RESEND_RECEIPT: {
    confirmLabel: "Yes, Send",
    heading: "Resend Receipt",
  },
};

const getDestructiveMessage = (
  kind: ManageInvoiceActionConfirmationKind,
  customerName: string,
) => {
  switch (kind) {
    case "CREDIT_REFUND":
      return `Are you sure you want to credit/refund the paid invoice for ${customerName}?`;
    case "DELETE_DRAFT":
      return `Are you sure you want to delete the draft invoice for ${customerName}?`;
    default:
      return null;
  }
};

const TrashIcon = ({ size }: { size: number }) => (
  <svg
    aria-hidden="true"
    fill="none"
    height={size}
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.8"
    viewBox="0 0 24 24"
    width={size}
  >
    <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M10 11v6M14 11v6" />
  </svg>
);

const CreditRefundIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="30"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.9"
    viewBox="0 0 24 24"
    width="30"
  >
    <path d="M6 3h12v17l-3-2-3 2-3-2-3 2z" />
    <path d="M14.5 12.5H10m0 0 2-2m-2 2 2 2" />
  </svg>
);

type ManageInvoiceActionConfirmationDialogProps = Readonly<{
  // RESEND_RECEIPT only: the address the payment already emailed a receipt to.
  alreadySentToEmail?: string | null;
  customerName: string;
  /** UX audit INV-11: true while the last send is Sending or Unknown. */
  emailDeliveryPending?: boolean;
  errorMessage?: string | null;
  kind: ManageInvoiceActionConfirmationKind;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  open: boolean;
  recipientEmail?: string | null;
  recipientLoading?: boolean;
}>;

const ManageInvoiceActionConfirmationDialog = ({
  alreadySentToEmail,
  customerName,
  emailDeliveryPending = false,
  errorMessage,
  kind,
  loading = false,
  onClose,
  onConfirm,
  open,
  recipientEmail,
  recipientLoading = false,
}: ManageInvoiceActionConfirmationDialogProps) => {
  const copy = copyByKind[kind];
  const [confirming, setConfirming] = useState(false);
  const [localErrorMessage, setLocalErrorMessage] = useState<string | null>(
    null,
  );
  const [localEmailDeliveryPending, setLocalEmailDeliveryPending] =
    useState(false);
  const confirmInFlightRef = useRef(false);
  const isPaymentConfirmation = kind === "PROCESS_PAYMENT";
  const isCreditRefundConfirmation = kind === "CREDIT_REFUND";
  const isDeleteDraftConfirmation = kind === "DELETE_DRAFT";
  // Destructive confirms use the design's red icon circle and red confirm.
  const isDestructiveConfirmation =
    isCreditRefundConfirmation || isDeleteDraftConfirmation;
  const isEmailConfirmation =
    kind === "EMAIL_INVOICE" ||
    kind === "EMAIL_RECEIPT" ||
    kind === "RESEND_RECEIPT";
  const effectiveLoading = loading || confirming;
  const validRecipientEmail = getValidInvoiceEmailRecipient(recipientEmail);
  const normalizedCustomerName = customerName.trim();
  const destructiveMessage = getDestructiveMessage(
    kind,
    normalizedCustomerName || "Customer",
  );
  const emailTarget = normalizedCustomerName
    ? `${normalizedCustomerName} (${validRecipientEmail || ""})`
    : validRecipientEmail || "";
  const emailConfirmationBlocked =
    isEmailConfirmation &&
    (!validRecipientEmail || emailDeliveryPending || localEmailDeliveryPending);
  const previousReceiptEmail =
    kind === "RESEND_RECEIPT"
      ? getValidInvoiceEmailRecipient(alreadySentToEmail) || validRecipientEmail
      : null;
  const resendTargetSuffix =
    previousReceiptEmail &&
    validRecipientEmail &&
    previousReceiptEmail.toLowerCase() !== validRecipientEmail.toLowerCase()
      ? ` to ${validRecipientEmail}`
      : "";

  useEffect(() => {
    if (open) {
      setConfirming(false);
      setLocalErrorMessage(null);
      setLocalEmailDeliveryPending(false);
      confirmInFlightRef.current = false;
    }
  }, [open, kind]);

  const handleConfirm = async () => {
    if (
      effectiveLoading ||
      emailConfirmationBlocked ||
      confirmInFlightRef.current
    ) {
      return;
    }

    setConfirming(true);
    setLocalErrorMessage(null);
    confirmInFlightRef.current = true;
    try {
      await onConfirm();
    } catch (error) {
      setLocalEmailDeliveryPending(isInvoiceEmailDeliveryPendingError(error));
      // An email may have gone out, so its unknown errors ask staff to
      // check first. Other actions show the task fallback.
      const fallback = isEmailConfirmation
        ? INVOICE_EMAIL_UNCONFIRMED_TEXT
        : buildStaffErrorFallback(confirmationTaskByKind[kind]);
      const text = getUserFacingErrorText(error, fallback);
      setLocalErrorMessage(
        isEmailConfirmation && text === NETWORK_ERROR_TEXT
          ? INVOICE_EMAIL_UNCONFIRMED_TEXT
          : text,
      );
    } finally {
      confirmInFlightRef.current = false;
      setConfirming(false);
    }
  };

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          ...invoiceDialogPaperSx({ maxWidth: 440 }),
          border: "1px solid #ECECEC",
        },
      }}
      onClose={effectiveLoading ? undefined : onClose}
    >
      <div
        className="relative bg-white px-7 py-[30px] text-center"
        style={{ fontFamily: invoiceDesignFontFamily }}
      >
        <InvoiceDialogCloseButton
          ariaLabel="Close confirmation"
          disabled={effectiveLoading}
          onClick={onClose}
        />

        <div
          className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full text-white ${
            isPaymentConfirmation || isDestructiveConfirmation
              ? "bg-[#F21B1B]"
              : "bg-[#0091FF]"
          }`}
        >
          {isCreditRefundConfirmation ? (
            <CreditRefundIcon />
          ) : isDeleteDraftConfirmation ? (
            <TrashIcon size={30} />
          ) : isPaymentConfirmation ? (
            <QuestionMarkOutlinedIcon sx={{ fontSize: 36 }} />
          ) : (
            <EmailOutlinedIcon sx={{ fontSize: 32 }} />
          )}
        </div>

        {copy.heading ? (
          <h2 className="mt-[18px] text-sm font-semibold leading-[normal] text-[#0A0A0A]">
            {copy.heading}
          </h2>
        ) : null}
        <p
          className={`mx-auto max-w-[340px] text-sm leading-[1.55] text-[#0A0A0A] ${
            isPaymentConfirmation ? "mt-[18px]" : "mt-2"
          }`}
        >
          {destructiveMessage
            ? destructiveMessage
            : isPaymentConfirmation
            ? "Are you sure you want to process payment for this invoice?"
            : validRecipientEmail && previousReceiptEmail
              ? `A receipt was already emailed to ${previousReceiptEmail}. Send another copy${resendTargetSuffix}?`
            : validRecipientEmail
              ? `You are about to email the ${
                  kind === "EMAIL_RECEIPT" ? "payment receipt" : "invoice"
                } to ${emailTarget}. Are you sure?`
              : recipientLoading
                ? "Loading the billing email address..."
                : getInvoiceNoSavedEmailText({
                    customerName: normalizedCustomerName,
                    document:
                      kind === "EMAIL_INVOICE" ? "invoice" : "payment receipt",
                    // Shared by Manage Invoices and the event sales pages,
                    // which use different print labels, so none is named.
                  })}
        </p>

        {localErrorMessage || errorMessage ? (
          <p className="mt-3 text-sm font-semibold text-red-600" role="alert">
            {localErrorMessage || errorMessage}
          </p>
        ) : null}

        <div className="mt-[22px] flex w-full flex-wrap justify-center gap-3">
          <Button
            disabled={effectiveLoading || emailConfirmationBlocked}
            onClick={() => void handleConfirm()}
            startIcon={
              effectiveLoading ? (
                <CircularProgress color="inherit" size={16} />
              ) : isDeleteDraftConfirmation ? (
                <TrashIcon size={17} />
              ) : undefined
            }
            sx={{
              ...invoiceModalActionSx({
                backgroundColor: isDestructiveConfirmation
                  ? invoiceDesignTokens.closeRed
                  : invoiceDesignTokens.blue,
                hoverBackgroundColor: isDestructiveConfirmation
                  ? "#D81313"
                  : "#0079D6",
              }),
              flex: "1 1 0",
              letterSpacing: "normal",
              lineHeight: "normal",
              maxWidth: 170,
              minHeight: 0,
              padding: "12px 20px",
            }}
            variant="contained"
          >
            {copy.confirmLabel}
          </Button>
          <Button
            disabled={effectiveLoading}
            onClick={onClose}
            sx={{
              ...invoiceModalActionSx({
                backgroundColor: invoiceDesignTokens.dark,
                hoverBackgroundColor: "#111827",
              }),
              flex: "1 1 0",
              letterSpacing: "normal",
              lineHeight: "normal",
              maxWidth: 170,
              minHeight: 0,
              padding: "12px 20px",
            }}
            variant="contained"
          >
            Cancel
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ManageInvoiceActionConfirmationDialog;
